import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useAppDispatch } from '@/app/store/hooks';
import { clearTokens, markSessionVerified } from '@/shared/lib/tokenStorage';

import { useLoginMutation } from './auth.api';
import { authenticated, signedOut } from './auth.slice';
import { decodeLinkToken, nextRouteFor } from './linkToken';

const INVALID_LINK_MESSAGE =
  'The link is invalid, please contact your agent to send you a new one.';

/**
 * Exchanges a magic link for a session.
 *
 * The sequence, and the reason for each step:
 *
 *   1. Clear any previous session. A director may open a second director's
 *      link on the same device, and a stale token would otherwise be sent.
 *   2. Decode and validate the link payload.
 *   3. `POST /director/login`, which stores the tokens.
 *   4. Branch: locked out, OTP required, or straight into the portal.
 *
 * Returns the error message for an unusable link; every other outcome is a
 * navigation.
 */
export const useLinkLogin = (): { errorMessage: string | null } => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login] = useLoginMutation();

  // Derived, not stored: whether the link is usable is a pure function of the
  // token in the URL. Keeping it in state would mean an effect writing a value
  // that is already knowable during render.
  const payload = token === undefined ? null : decodeLinkToken(token);
  const errorMessage = token !== undefined && payload === null ? INVALID_LINK_MESSAGE : null;

  // StrictMode double-invokes effects in development, and this one is a
  // single-use token exchange: running it twice spends the token and the second
  // call legitimately fails.
  const exchanged = useRef(false);

  useEffect(() => {
    if (token === undefined || exchanged.current) return;
    exchanged.current = true;

    // Step 1 — never carry a previous director's session into this exchange.
    clearTokens();
    dispatch(signedOut());

    if (payload === null) {
      dispatch(signedOut(INVALID_LINK_MESSAGE));
      void navigate('/logout', { replace: true });
      return;
    }

    const run = async (): Promise<void> => {
      try {
        const result = await login({ payload }).unwrap();
        const destination = nextRouteFor(payload, window.screen.width, window.location.search);

        if (result.dPLocked) {
          void navigate('/locked', { replace: true });
          return;
        }

        if (result.director2FA) {
          void navigate('/otp', {
            replace: true,
            state: {
              asicMemberId: payload.asicMemberId,
              nextRoute: destination,
              otpExpiry: result.otpExpiry,
            },
          });
          return;
        }

        // No second factor configured: the login itself is the whole gate.
        markSessionVerified();
        dispatch(authenticated());
        void navigate(destination, { replace: true });
      } catch {
        void navigate('/error', { replace: true });
      }
    };

    void run();
  }, [token, payload, dispatch, navigate, login]);

  return { errorMessage };
};
